import React, { useEffect, useRef, useState } from 'react';
import {
 Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';

/*
 * Wording follows live-captions/SCRIPT_STATUS_MESSAGES-2026-10-02.md. Rules that matter here:
 *  - a chip leads with what is on air, then health; "Script on air" only when the script really is;
 *  - never "locked", "verified" or "accurate": the tracker believes it is in place, it has not checked the text;
 *  - falling back to speech recognition is normal and safe, so it is neutral, never an error;
 *  - whole sentences per state, so each one translates as a unit; no internal vocabulary.
 */
export const MODES = [
 { value: 'shadow', label: 'Shadow', detail: 'Follows the script in the background while speech recognition stays on air. The safe way to try it.' },
 { value: 'follow', label: 'Follow', detail: 'Speech recognition stays on air, with names and terms corrected from your script as they are spoken.' },
 { value: 'run', label: 'Run', detail: "Airs the script itself, on its own clock, with speech recognition checking it stays in place. Speech recognition takes over when it can't." },
];

const PAUSE_NOTE = 'Captions pause for a few seconds while this applies. You can go back to speech recognition at any time.';
const VALUE_LINE = 'Prepared scripts can bring exact spellings, speaker names and music notes to live captions. Off script, speech recognition carries on.';

/**
 * The operator-facing state, from the gateway status. Pure, so every row of the message table is testable.
 * ctx.captionsOn: true/false when the channel's caption state is known; ctx.error: {down} when the status
 * could not be read ({down: true} when the gateway itself is not answering).
 */
export function describeFollowing(status, ctx = {}) {
 const { captionsOn, error } = ctx;
 if (error && error.down) {
  return { phase: 'stopped', color: 'error', label: 'Speech on air; script stopped',
   detail: 'Script following stopped. Captions continue as plain speech recognition. Start it again when you are ready.' };
 }
 if (error) {
  return { phase: 'unavailable', color: 'warning', label: 'Status unavailable',
   detail: "Can't read the current state. This does not change what is on air." };
 }
 if (!status) return { phase: 'unknown', color: 'default', label: 'Checking status', detail: '' };
 if (status.proxy !== 'provisioned') {
  if (!(status.rundown_entries || []).length) {
   return { phase: 'norundown', color: 'default', label: 'Not ready: no script saved', detail: 'Save a script in the rundown below, then choose a mode.' };
  }
  return { phase: 'off', color: 'default', label: 'Off: speech recognition on air',
   detail: 'Captions use speech recognition only. Choose a mode to bring in a script.' };
 }
 if (captionsOn === false) {
  return { phase: 'captions-off', color: 'warning', label: 'Captions are off for this channel',
   detail: 'Nothing is captioned. Script following is kept and resumes when captions are on.' };
 }
 const onScript = status.source === 'script' || status.source === 'mixed';
 switch (status.mode) {
  case 'asr_only':
   return { phase: 'armed', color: 'default', label: 'Ready: speech recognition on air',
    detail: 'Script following is set up. Nothing changes on air until you pick Follow or Run.' };
  case 'shadow':
   return status.state === 'LOCKED'
    ? { phase: 'shadow', color: 'success', label: 'Shadow: speech on air, script tracked', detail: 'The script is followed in the background. What is on air is unchanged.' }
    : { phase: 'shadow-searching', color: 'default', label: 'Shadow: speech on air, finding place', detail: 'Waiting for speech that matches the script. What is on air is unchanged.' };
  case 'follow':
   return onScript
    ? { phase: 'following', color: 'success', label: 'Follow: script corrections on air', detail: 'Speech recognition is on air, with names and terms corrected from the script.' }
    : { phase: 'off-script', color: 'default', label: 'Follow: speech on air, off script',
     detail: 'Speech is not matching the script, so captions are plain speech recognition. It picks the script up again when speech matches.' };
  case 'run': {
   if (status.ended) {
    return { phase: 'ended', color: 'default', label: 'Speech on air; script ended', detail: 'The script has ended. Captions continue as plain speech recognition.' };
   }
   if (status.run_state === 'RUNNING' || (status.run_state === undefined && onScript)) {
    return { phase: 'running', color: 'success', label: 'Script on air', detail: 'The script is airing on its own clock. Speech recognition is checking it stays in place.' };
   }
   if (status.run_state === 'FALLBACK') {
    if (status.fallback_reason === 'silence') {
     return { phase: 'fallback-silence', color: 'default', label: 'Speech on air; waiting for speech', detail: 'Speech recognition took over during the quiet. The script returns when speaking resumes.' };
    }
    if (status.fallback_reason === 'mismatch') {
     return { phase: 'fallback-mismatch', color: 'default', label: 'Speech on air; speech left the script', detail: 'Speech recognition took over. This is normal. The script returns when speech matches.' };
    }
    return { phase: 'fallback', color: 'default', label: 'Speech on air; script paused', detail: 'Speech recognition took over. The script returns by itself when speech matches.' };
   }
   return { phase: 'searching', color: 'warning', label: 'Speech on air; finding the script', detail: 'Speech recognition is on air. The script goes on air when speech matches it.' };
  }
  default:
   return { phase: 'unknown', color: 'default', label: 'Checking status', detail: '' };
 }
}

/** Plain-language messages for server refusals. Raw server text is never shown. */
export function friendlyError(failure, { max } = {}) {
 const text = `${failure?.detail || failure?.message || ''}`;
 if (/save a rundown/i.test(text)) return 'Save a script in the rundown first, then start.';
 if (/captions must be enabled/i.test(text)) return 'Turn captions on for this channel first.';
 if (/not enabled on this deployment/i.test(text)) return "Script following isn't available on this account.";
 if (failure?.status === 503 || /temporarily unavailable|unavailable/i.test(text)) {
  return 'Script following is temporarily unavailable. Try again shortly.';
 }
 return "Couldn't change script following. Check that captions are still on air, then try again or contact support.";
}

const positionMessage = max => (Number.isInteger(max) && max >= 0
 ? `That word number isn't in the script. Enter a number from 0 to ${max}.`
 : "That word number isn't in the script. Enter a smaller number.");
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function ScriptFollowingCard({ channelId, getFollowing, getCaptionsState, activate, deactivate, control, onStatus, pollMs = 2000 }) {
 const [status, setStatus] = useState(null);
 const [statusError, setStatusError] = useState(null);
 const [captionsOn, setCaptionsOn] = useState(undefined);
 const [mode, setMode] = useState('shadow');
 const [confirming, setConfirming] = useState(false);
 const [busy, setBusy] = useState('');
 const [error, setError] = useState('');
 const [notice, setNotice] = useState('');
 const [word, setWord] = useState('0');
 const api = useRef({});
 api.current = { getFollowing, getCaptionsState, activate, deactivate, control, onStatus };
 const touched = useRef(false);   // once the operator picks a mode, the live status stops choosing for them

 useEffect(() => {
  let current = true;
  async function tick() {
   try {
    const next = await api.current.getFollowing(channelId);
    if (!current) return;
    setStatus(next); setStatusError(null);
    if (!touched.current && next?.proxy === 'provisioned' && MODES.some(item => item.value === next.mode)) setMode(next.mode);
    if (api.current.onStatus) api.current.onStatus(next);
   } catch (failure) {
    if (current) setStatusError({ down: failure?.status === 502 && /gateway unavailable/i.test(`${failure?.detail || ''}`) });
   }
   if (api.current.getCaptionsState) {
    try {
     const state = await api.current.getCaptionsState(channelId);
     if (current) setCaptionsOn(['starting', 'live', 'idle'].includes(state));
    } catch (failure) { /* the caption state is context only; its absence changes no message */ }
   }
  }
  tick();
  const timer = setInterval(tick, pollMs);
  return () => { current = false; clearInterval(timer); };
 }, [channelId, pollMs]);

 const view = describeFollowing(status, { captionsOn, error: statusError });
 const active = status?.proxy === 'provisioned';
 const chosen = MODES.find(item => item.value === mode);
 const max = Number.isInteger(status?.length) ? status.length - 1 : undefined;
 const wordValid = /^\d+$/.test(word) && (max === undefined || Number(word) <= max);

 async function run(label, action, success, mapError = friendlyError) {
  setBusy(label); setError(''); setNotice('');
  try { await action(); setNotice(success); setStatus(await api.current.getFollowing(channelId)); }
  catch (failure) { setError(mapError(failure, { max })); }
  finally { setBusy(''); }
 }

 return <Paper sx={{ p: 2 }} aria-label="Script following">
  <Stack spacing={1.5}>
   <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
    <Typography variant="h6" component="h2">Script following</Typography>
    <Chip color={view.color} label={view.label} role="status" aria-live="polite" />
   </Stack>
   {view.detail && <Typography variant="body2">{view.detail}</Typography>}
   {view.phase === 'off' && <Typography variant="caption">{VALUE_LINE}</Typography>}
   {active && !statusError && (status.entries || []).length > 0 && view.phase !== 'captions-off' && (
    <Typography variant="body2">{plural(status.entries.length, 'script', 'scripts')} in the rundown.</Typography>
   )}
   {!active && status && (status.rundown_entries || []).length > 0 && (
    <Typography variant="body2">{plural(status.rundown_entries.length, 'script', 'scripts')} in the rundown.</Typography>
   )}
   {error && <Alert severity="error" role="alert">{error}</Alert>}
   {notice && <Alert severity="success" role="status">{notice}</Alert>}
   <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
    <TextField select size="small" label="Mode" value={mode} onChange={event => { touched.current = true; setMode(event.target.value); }} sx={{ minWidth: 140 }} disabled={Boolean(busy)}>
     {MODES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
    </TextField>
    <Button variant="contained" disabled={Boolean(busy) || (active && status.mode === mode)} onClick={() => setConfirming(true)}>
     {active ? 'Switch mode' : 'Start'}
    </Button>
    <Button color="warning" variant="outlined" disabled={!active || Boolean(busy)} title="Turns script following off. Captions continue without it."
            onClick={() => run('Stopping…', () => api.current.deactivate(channelId), 'Back to speech recognition.')}>
     Use speech recognition
    </Button>
   </Stack>
   <Typography variant="caption">{chosen.detail}</Typography>
   {active && status.mode === 'run' && <Stack direction="row" spacing={1} alignItems="flex-start">
    <TextField size="small" type="number" label="Script position (word number)" value={word} onChange={event => setWord(event.target.value)}
               sx={{ width: 260 }} inputProps={{ min: 0, max }} error={!wordValid && word !== ''}
               helperText={!wordValid && word !== '' ? positionMessage(max) : 'Use if the script is in the wrong place. Enter the word number it should be at.'} />
    <Button disabled={Boolean(busy) || !wordValid}
            onClick={() => run('Setting position…', () => api.current.control(channelId, { op: 'reanchor', word_index: Number(word) }), 'Position set.',
             (failure, ctx) => ([400, 409, 422].includes(failure?.status) ? positionMessage(ctx.max) : friendlyError(failure, ctx)))}>
     Set position
    </Button>
   </Stack>}
   {busy && <Typography role="status">{busy}</Typography>}
  </Stack>
  <Dialog open={confirming} onClose={() => setConfirming(false)} aria-labelledby="script-following-confirm">
   <DialogTitle id="script-following-confirm">{active ? `Switch to ${chosen.label} mode?` : `Start in ${chosen.label} mode?`}</DialogTitle>
   <DialogContent>
    <Typography>Channel {channelId}</Typography>
    <Typography sx={{ mt: 1 }}>{chosen.detail}</Typography>
    <Typography sx={{ mt: 1 }}>{PAUSE_NOTE}</Typography>
   </DialogContent>
   <DialogActions>
    <Button onClick={() => setConfirming(false)}>Cancel</Button>
    <Button variant="contained" onClick={() => {
     setConfirming(false);
     run(active ? 'Switching mode…' : 'Starting…', () => api.current.activate(channelId, mode), `${chosen.label} mode is on.`);
    }}>
     {active ? 'Switch' : 'Start'}
    </Button>
   </DialogActions>
  </Dialog>
 </Paper>;
}
